import { z } from 'zod';
import { MoneySchema, PrintMethodSchema, RectMmSchema, ViewIdSchema, } from './design.js';
export const CategorySchema = z.lazy(() => z.object({
    id: z.string(),
    label: z.string(),
    icon: z.string().optional(),
    children: z.array(CategorySchema).optional(),
}));
export const ModelSummarySchema = z.object({
    id: z.string(),
    name: z.string(),
    thumbnail: z.string(),
    colorCount: z.number().int().nonnegative(),
    priceFrom: MoneySchema,
    badge: z.string().optional(),
    categoryId: z.string().optional(),
});
export const ModelColorSchema = z.object({
    key: z.string(),
    label: z.string(),
    hex: z.string(),
    isDark: z.boolean(),
    mockups: z.record(ViewIdSchema, z.object({
        url: z.string(),
        pxPerMm: z.number().positive(),
    })),
    available: z.boolean(),
});
export const ModelSizeSchema = z.object({
    id: z.string(),
    label: z.string(),
    order: z.number().int(),
});
export const ModelVariantSchema = z.object({
    id: z.string(),
    colorKey: z.string(),
    sizeId: z.string(),
    availability: z.enum(['in_stock', 'out_of_stock', 'backorder']),
    availabilityLabel: z.string().optional(),
    price: MoneySchema.optional(),
});
export const ProductModelSchema = z.object({
    id: z.string(),
    name: z.string(),
    category: z.object({ id: z.string(), label: z.string() }),
    rating: z.object({ value: z.number(), count: z.number().int() }).optional(),
    templateId: z.string(),
    views: z.array(z.object({ id: ViewIdSchema, label: z.string() })),
    printAreas: z.record(ViewIdSchema, RectMmSchema),
    printMethod: PrintMethodSchema,
    minDpi: z.number().positive(),
    colors: z.array(ModelColorSchema),
    sizes: z.array(ModelSizeSchema),
    sizeRangeLabel: z.string(),
    /** Woo attribute label for the size axis (Talla, Tamaño, …). */
    sizeAxisLabel: z.string().optional(),
    colorAxisLabel: z.string().optional(),
    /** When false, UI hides size rows (single Única / no real options). */
    showSizes: z.boolean().optional(),
    /** When false, UI hides color swatches (single product/color). */
    showColors: z.boolean().optional(),
    sizeGuideUrl: z.string().optional(),
    variants: z.array(ModelVariantSchema),
    priceFrom: MoneySchema,
    priceTo: MoneySchema.optional(),
    promoNote: z
        .object({
        text: z.string(),
        infoUrl: z.string().optional(),
        /** Modal title when clicking “más info”. */
        modalTitle: z.string().optional(),
        /** Rich HTML body for the promo modal (preferred). */
        modalHtml: z.string().optional(),
        /** Bullet / paragraph lines (fallback if modalHtml is empty). */
        modalLines: z.array(z.string()).optional(),
        modalFootnote: z.string().optional(),
        /** When false, hide configured HTML/lines; show live offers only. */
        showCustomText: z.boolean().optional(),
        /** Deploy/debug marker from Ox_Designer_Offers. */
        offersBuild: z.string().optional(),
        /** Live WCDPD volume tiers for this product. */
        volumeTiers: z
            .array(z.object({
            from: z.number().int().positive(),
            to: z.number().int().positive().nullable().optional(),
            label: z.string(),
            unitPrice: MoneySchema,
            /** Catalog / pre-discount unit price when a simple or volume discount applies. */
            regularPrice: MoneySchema.optional(),
            discountLabel: z.string().optional(),
            /** Winning WCDPD method: simple | bulk | tiered */
            kind: z.string().optional(),
        }))
            .optional(),
        /** Member vs catalog price for the current viewer. */
        membership: z
            .object({
            active: z.boolean(),
            regularPrice: MoneySchema,
            memberPrice: MoneySchema,
        })
            .optional(),
        /** Member vs public print size fees for the model. */
        printTiers: z
            .array(z.object({
            maxSideCm: z.number().positive(),
            label: z.string(),
            regularPrice: MoneySchema,
            memberPrice: MoneySchema,
        }))
            .optional(),
    })
        .optional(),
    /** Rich HTML for the shipping / delivery box in the footer. */
    deliveryHtml: z.string().optional(),
    deliveryEstimates: z
        .array(z.object({ label: z.string(), value: z.string() }))
        .optional(),
    /** Model is a print-only service (garment type selector). */
    printService: z.boolean().optional(),
    /** Ask garment print type on PDP before opening the designer. */
    askGarmentPrintType: z.boolean().optional(),
    /** Customer brings their own garment (ask color on PDP; print size + note in cart modal). */
    askGarmentColor: z.boolean().optional(),
    /** Available garment sizes for customer-supplied apparel. */
    printGarmentSizes: z.array(z.string().min(1)).optional(),
    /** Fixed surcharge amount when franela reactiva is checked (0 = off). */
    franelaSurcharge: z.number().nonnegative().optional(),
    /**
     * Sibling models for the same products with different print methods.
     * When length > 1, the editor can switch DTG/DTF.
     */
    printMethodChoices: z
        .array(z.object({
        method: PrintMethodSchema,
        modelId: z.string(),
        label: z.string(),
    }))
        .optional(),
    /** Prompt for print method in the builder (multi-method, no Woo attr, PDP gate off). */
    askPrintMethod: z.boolean().optional(),
});
export const QuoteLineInputSchema = z.object({
    variantId: z.string(),
    quantity: z.number().int().positive(),
});
export const ViewMetricSchema = z.object({
    viewId: z.string(),
    label: z.string().optional(),
    widthMm: z.number().positive(),
    heightMm: z.number().positive(),
    objectCount: z.number().int().positive(),
});
export const PriceBreakdownLineSchema = z.object({
    code: z.string(),
    label: z.string(),
    amount: z.number(),
    included: z.boolean().optional(),
    viewId: z.string().optional(),
    widthMm: z.number().optional(),
    heightMm: z.number().optional(),
});
export const QuotedLineSchema = z.object({
    variantId: z.string(),
    quantity: z.number().int().positive(),
    unitPrice: MoneySchema,
    lineTotal: MoneySchema,
    label: z.string().optional(),
});
export const DiscountSchema = z.object({
    label: z.string(),
    amount: MoneySchema,
});
export const QuoteResultSchema = z.object({
    lines: z.array(QuotedLineSchema),
    subtotal: MoneySchema,
    discounts: z.array(DiscountSchema).default([]),
    extras: MoneySchema.optional(),
    extrasUnit: MoneySchema.optional(),
    breakdown: z.array(PriceBreakdownLineSchema).optional(),
    total: MoneySchema,
    note: z.string().optional(),
});
export const CartAddResultSchema = z.object({
    redirectUrl: z.string().optional(),
    cartCount: z.number().int().nonnegative(),
});
//# sourceMappingURL=commerce.js.map